import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Building2, Briefcase, Mail, Lock, Phone, Globe, Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { b2bApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const B2BRegisterPage = () => {
  const navigate = useNavigate();
  const { setUser: setAuthUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    company_name: '', partner_type: '', company_address: '',
    company_website: '', gst_number: '', expected_monthly_volume: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.partner_type) { toast.error('Please select partner type'); return; }
    if (!form.company_name) { toast.error('Please enter company name'); return; }
    setLoading(true);
    try {
      const res = await b2bApi.register(form);
      localStorage.setItem('access_token', res.data.access_token);
      if (setAuthUser) setAuthUser(res.data.user);
      toast.success('B2B registration successful! Awaiting admin approval.');
      navigate('/partner/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-[#F5F0E6] flex">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          {/* <Link to="/" className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-[#FF5A36] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">E</span>
            </div>
            <span className="font-bold text-xl text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>EasyMyVisa</span>
          </Link> */
          }
           <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">        
                            <img src="/images/logo.png" alt="EasyMyVisa Logo" className=""  style={{ width: '250px', height: '150px' }} />
          </Link>

          <h1 className="text-2xl font-bold text-[#0A1E40] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>B2B Partner Registration</h1>
          <p className="text-[#4A5568] mb-6 text-sm">Register as a travel agent or corporate partner. Applications require admin approval.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="b2b-register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Partner Type *</Label>
                <Select value={form.partner_type} onValueChange={(v) => update('partner_type', v)}>
                  <SelectTrigger data-testid="partner-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent"><span className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" /> Travel Agent</span></SelectItem>
                    <SelectItem value="corporate"><span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Corporate</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Company Name *</Label>
                <Input value={form.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder="Your company" required data-testid="company-name-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Contact Person *</Label>
                <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Full name" required data-testid="b2b-name-input" />
              </div>
              <div>
                <Label className="text-xs font-medium">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="pl-10" placeholder="business@company.com" required data-testid="b2b-email-input" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => update('password', e.target.value)} className="pl-10 pr-10" placeholder="Min 6 characters" required data-testid="b2b-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                  <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="pl-10" placeholder="+91 XXXXX" data-testid="b2b-phone-input" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium">Company Address</Label>
              <Textarea value={form.company_address} onChange={(e) => update('company_address', e.target.value)} placeholder="Full address" rows={2} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium">Website</Label>
                <Input value={form.company_website} onChange={(e) => update('company_website', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label className="text-xs font-medium">GST Number</Label>
                <Input value={form.gst_number} onChange={(e) => update('gst_number', e.target.value)} placeholder="GSTIN" />
              </div>
              <div>
                <Label className="text-xs font-medium">Monthly Volume</Label>
                <Select value={form.expected_monthly_volume} onValueChange={(v) => update('expected_monthly_volume', v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10</SelectItem>
                    <SelectItem value="11-50">11-50</SelectItem>
                    <SelectItem value="51-200">51-200</SelectItem>
                    <SelectItem value="200+">200+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-full py-6 font-semibold" data-testid="b2b-register-btn">
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Registering...</> : <>Register as B2B Partner <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#4A5568]">
            Already have an account? <Link to="/partner/login" className="text-[#FF5A36] font-semibold hover:underline">Sign In</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-2/5 relative">
        <div className="absolute inset-0 bg-[#0A1E40]" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-sm">
            <Building2 className="w-16 h-16 mx-auto mb-6 text-[#FF5A36]" />
            <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Grow Your Business</h2>
            <p className="text-white/70 mb-6">Submit visa applications for your clients, get special pricing, and track everything from one dashboard.</p>
            <div className="space-y-3 text-left">
              {['Bulk application submission', 'Special partner discounts', 'Commission on each application', 'Dedicated partner dashboard'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-white/80 text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#FF5A36]/20 flex items-center justify-center shrink-0">
                    <span className="text-[#FF5A36] text-xs font-bold">{i + 1}</span>
                  </div>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BRegisterPage;
